import React, { useRef, useState, useEffect } from 'react';

interface TestimonialProps {
  id: number;
  name: string;
  image: string;
  rating: number;
  quote: string;
}

const testimonials: TestimonialProps[] = [
  {
    id: 1,
    name: "George Russell",
    image: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=256&q=80",
    rating: 5,
    quote: "The lessons are easy to follow, culturally relevant, and suitable for beginners. Highly recommended for anyone who wants to learn Indonesian effectively.",
  },
  {
    id: 2,
    name: "Alex Albon",
    image: "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?auto=format&fit=crop&w=256&q=80",
    rating: 5,
    quote: "I’ve tried learning Indonesian before, but this platform made it so much easier. The explanations are clear, practical, and really focused on daily conversations.",
  },
  {
    id: 3,
    name: "Olivia Isabel Rodrigo",
    image: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=256&q=80",
    rating: 5,
    quote: "What I like the most is how this platform teaches real Indonesian, not just textbook language. It helped me understand how people actually speak in daily situations.",
  },
  {
    id: 4,
    name: "Oscar Piastri",
    image: "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?auto=format&fit=crop&w=256&q=80",
    rating: 5,
    quote: "Fluenesia’s structured levels helped me balance learning Indonesian with my busy travel schedule. The tutors are incredibly patient and insightful.",
  },
  {
    id: 5,
    name: "Charles Leclerc",
    image: "https://images.unsplash.com/photo-1492562080023-ab3db95bfbce?auto=format&fit=crop&w=256&q=80",
    rating: 5,
    quote: "The interactive speaking sessions boosted my confidence from day one. Learning slang and local mannerisms made all the difference when visiting Jakarta.",
  },
  {
    id: 6,
    name: "Dua Lipa",
    image: "https://images.unsplash.com/photo-1517841905240-472988babdf9?auto=format&fit=crop&w=256&q=80",
    rating: 5,
    quote: "Amazing cultural immersion and personalized pace! I loved how practical the vocabulary was for connecting with locals and exploring Indonesian culture.",
  },
];

const Testimonials: React.FC = () => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);

  // Memeriksa posisi scroll untuk menentukan apakah panah kiri/kanan hilang saat mentok
  const checkScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      
      // Panah kiri HILANG saat mentok di awal (scrollLeft <= 15)
      setCanScrollLeft(scrollLeft > 15);
      
      // Panah kanan HILANG saat mentok di ujung akhir
      setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 15);

      // Sinkronisasi active dot (3 dot pagination)
      const maxScroll = scrollWidth - clientWidth;
      if (maxScroll > 0) {
        const ratio = scrollLeft / maxScroll;
        const page = Math.min(2, Math.max(0, Math.round(ratio * 2)));
        setActiveIndex(page);
      }
    }
  };

  useEffect(() => {
    // Jalankan pengecekan posisi awal setelah render
    const timer = setTimeout(checkScroll, 100);
    const el = scrollRef.current;
    if (el) {
      el.addEventListener('scroll', checkScroll, { passive: true });
    }
    window.addEventListener('resize', checkScroll);
    return () => {
      clearTimeout(timer);
      if (el) el.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
    };
  }, []);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const { scrollLeft, clientWidth } = scrollRef.current;
      // Geser kira-kira 1-2 kartu sekaligus
      const scrollStep = clientWidth > 768 ? 420 : clientWidth * 0.85;
      const targetScroll =
        direction === 'left' ? scrollLeft - scrollStep : scrollLeft + scrollStep;
      scrollRef.current.scrollTo({ left: targetScroll, behavior: 'smooth' });
    }
  };

  const scrollToPage = (pageIndex: number) => {
    if (scrollRef.current) {
      const { scrollWidth, clientWidth } = scrollRef.current;
      const maxScroll = scrollWidth - clientWidth;
      if (maxScroll > 0) {
        const target = (maxScroll / 2) * pageIndex;
        scrollRef.current.scrollTo({ left: target, behavior: 'smooth' });
      }
      setActiveIndex(pageIndex);
    }
  };

  return (
    <section className="w-full bg-[#FDFDFD] py-20 lg:py-24 px-4 sm:px-6 relative overflow-hidden">
      <div className="max-w-[1320px] mx-auto relative px-2 sm:px-8">
        {/* --- Header Section (Frame 11 di Figma) --- */}
        <div
          className="flex flex-col items-center text-center max-w-[624px] mx-auto mb-16 lg:mb-20"
          data-aos="fade-up"
          data-aos-duration="1000"
        >
          {/* Badge: Testimonials */}
          <div className="inline-flex items-center px-3 py-1 bg-[#FEE6E6] border border-[#C60302] rounded-full mb-4">
            <span className="font-poppins font-normal text-xs text-[#C60302] leading-none">
              Testimonials
            </span>
          </div>

          {/* Headline */}
          <h2 className="font-montserrat font-bold text-3xl sm:text-4xl lg:text-[40px] text-[#202020] leading-[1.2] lg:leading-[48px] tracking-tight mb-4">
            What Students Think and Say About Fluenesia?
          </h2>

          {/* Subtitle */}
          <p className="font-montserrat font-normal text-base sm:text-lg lg:text-[20px] text-[#6A6A6A] leading-relaxed lg:leading-[28px]">
            Real stories from learners around the world who achieved fluency with our structured programs.
          </p>
        </div>

        {/* --- Navigation Buttons (Melayang di samping kartu) --- */}
        {/* Panah Kiri: Hilang saat mentok di awal */}
        <button
          onClick={() => scroll('left')}
          disabled={!canScrollLeft}
          aria-label="Previous Testimonials"
          className={`absolute left-0 top-[55%] -translate-y-1/2 z-20 w-12 h-12 bg-white rounded-full shadow-[1px_2px_6px_rgba(32,32,32,0.25)] flex items-center justify-center transition-all duration-300 ${
            canScrollLeft
              ? 'opacity-100 scale-100 pointer-events-auto hover:bg-gray-50 active:scale-95'
              : 'opacity-0 scale-75 pointer-events-none'
          }`}
        >
          <svg
            className="w-6 h-6 text-[#C60302]"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>

        {/* Panah Kanan: Hilang saat mentok di akhir */}
        <button
          onClick={() => scroll('right')}
          disabled={!canScrollRight}
          aria-label="Next Testimonials"
          className={`absolute right-0 top-[55%] -translate-y-1/2 z-20 w-12 h-12 bg-white rounded-full shadow-[1px_2px_6px_rgba(32,32,32,0.25)] flex items-center justify-center transition-all duration-300 ${
            canScrollRight
              ? 'opacity-100 scale-100 pointer-events-auto hover:bg-gray-50 active:scale-95'
              : 'opacity-0 scale-75 pointer-events-none'
          }`}
        >
          <svg
            className="w-6 h-6 text-[#C60302]"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>

        {/* --- Testimonials Cards Carousel (Frame 12 di Figma) --- */}
        <div
          ref={scrollRef}
          className="flex overflow-x-auto gap-6 sm:gap-7 pb-4 pt-1 snap-x snap-mandatory hide-scroll-bar items-stretch"
        >
          {testimonials.map((item, idx) => (
            <div
              key={item.id}
              className="bg-white rounded-[24px] border border-[#C60302] p-7 sm:p-8 flex-shrink-0 w-[300px] sm:w-[370px] lg:w-[397px] min-h-[374px] snap-start flex flex-col justify-between hover:shadow-lg transition-all duration-300"
              data-aos="fade-up"
              data-aos-delay={(idx % 3) * 100}
              data-aos-duration="1000"
            >
              {/* Sisi Atas: Ikon Kutip & Teks Ulasan */}
              <div>
                {/* Ikon Kutip Merah Kiri Atas (icomoon-free:quotes-left) */}
                <svg
                  className="w-12 h-12 text-[#C60302] mb-5 flex-shrink-0"
                  viewBox="0 0 32 32"
                  fill="currentColor"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M0 18c0-6.627 5.373-12 12-12v4c-4.418 0-8 3.582-8 8h8v12h-12v-12zM16 18c0-6.627 5.373-12 12-12v4c-4.418 0-8 3.582-8 8h8v12h-12v-12z" />
                </svg>

                {/* Teks Quote */}
                <p className="font-montserrat font-normal text-base sm:text-[18px] text-[#202020] leading-relaxed lg:leading-[28px]">
                  {item.quote}
                </p>
              </div>

              {/* Sisi Bawah: Foto Avatar Riil, Nama Murid, & 5 Bintang Emas */}
              <div className="flex items-center gap-4 pt-6 mt-4 border-t border-gray-100">
                <img
                  src={item.image}
                  alt={item.name}
                  className="w-14 h-14 rounded-full object-cover flex-shrink-0 shadow-sm border border-gray-100"
                />
                <div className="flex flex-col">
                  {/* Nama Murid */}
                  <h4 className="font-montserrat font-semibold text-lg sm:text-[19px] text-[#202020] leading-snug">
                    {item.name}
                  </h4>

                  {/* 5 Bintang Rating Emas (#FFCC00) */}
                  <div className="flex items-center gap-1 mt-1">
                    {[...Array(item.rating)].map((_, starIdx) => (
                      <svg
                        key={starIdx}
                        className="w-4 h-4 fill-[#FFCC00] text-[#FFCC00]"
                        viewBox="0 0 24 24"
                      >
                        <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                      </svg>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* --- Dots Pagination Riil di Bawah (3 Titik) --- */}
        <div className="flex items-center justify-center gap-2 mt-10">
          {[0, 1, 2].map((dotIdx) => (
            <button
              key={dotIdx}
              onClick={() => scrollToPage(dotIdx)}
              aria-label={`Slide ${dotIdx + 1}`}
              className={`h-2 rounded-full transition-all duration-300 cursor-pointer ${
                activeIndex === dotIdx
                  ? 'w-9 bg-[#C60302]'
                  : 'w-2 bg-[#D9D9D9] hover:bg-gray-400'
              }`}
            />
          ))}
        </div>
      </div>

      <style>{`
        .hide-scroll-bar::-webkit-scrollbar { display: none; }
        .hide-scroll-bar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </section>
  );
};

export default Testimonials;
